import type {
  IWorkJourneyManagementCalendarCell,
  IWorkJourneyManagementCalendarDayData,
  TWorkJourneyManagementCalendarDayHighlight,
  TWorkJourneyManagementCalendarFooterAction,
} from '@src/models/interfaces/workJourneyManagement.interface';

interface ICalendarDateParts {
  year: number;
  month: number;
  day: number;
}

const formatISODate = ({ year, month, day }: ICalendarDateParts): string =>
  new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));

const getMondayBasedWeekdayIndex = (date: Date): number => {
  const weekday = date.getDay();
  return weekday === 0 ? 6 : weekday - 1;
};

const isSameCalendarDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const getDefaultHighlight = (
  cellDate: Date,
  today: Date,
  isCurrentMonth: boolean,
): TWorkJourneyManagementCalendarDayHighlight => {
  if (!isCurrentMonth) return 'normal';
  if (isSameCalendarDay(cellDate, today)) return 'today';
  return 'normal';
};

const getDefaultFooterAction = (
  cellDate: Date,
  today: Date,
  isCurrentMonth: boolean,
): TWorkJourneyManagementCalendarFooterAction => {
  if (!isCurrentMonth) return 'journeys';
  const todayAtMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const cellAtMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
  return cellAtMidnight < todayAtMidnight ? 'report' : 'journeys';
};

const buildDefaultDayData = (
  dateParts: ICalendarDateParts,
  isCurrentMonth: boolean,
  today: Date,
): IWorkJourneyManagementCalendarDayData => {
  const cellDate = new Date(dateParts.year, dateParts.month - 1, dateParts.day);

  return {
    date: formatISODate(dateParts),
    indicators: [],
    journeyCount: 0,
    footerAction: getDefaultFooterAction(cellDate, today, isCurrentMonth),
    highlight: getDefaultHighlight(cellDate, today, isCurrentMonth),
    conflicts: [],
  };
};

export const buildWorkJourneyCalendarGrid = (
  month: number,
  year: number,
  days: IWorkJourneyManagementCalendarDayData[],
  today: Date = new Date(),
): IWorkJourneyManagementCalendarCell[] => {
  const daysByDate = new Map(days.map(day => [day.date, day]));
  const firstOfMonth = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const leadingEmptyCells = getMondayBasedWeekdayIndex(firstOfMonth);

  const previousMonth = month === 1 ? 12 : month - 1;
  const previousYear = month === 1 ? year - 1 : year;
  const daysInPreviousMonth = new Date(previousYear, previousMonth, 0).getDate();

  const cells: IWorkJourneyManagementCalendarCell[] = [];

  for (let index = leadingEmptyCells; index > 0; index -= 1) {
    const day = daysInPreviousMonth - index + 1;
    const dateParts = { year: previousYear, month: previousMonth, day };
    const date = formatISODate(dateParts);
    const dayData = daysByDate.get(date) ?? buildDefaultDayData(dateParts, false, today);

    cells.push({
      ...dayData,
      day,
      isCurrentMonth: false,
      isToday: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateParts = { year, month, day };
    const date = formatISODate(dateParts);
    const cellDate = new Date(year, month - 1, day);
    const dayData = daysByDate.get(date) ?? buildDefaultDayData(dateParts, true, today);

    const highlight =
      dayData.highlight === 'normal' && isSameCalendarDay(cellDate, today)
        ? 'today'
        : dayData.highlight;

    cells.push({
      ...dayData,
      day,
      isCurrentMonth: true,
      isToday: isSameCalendarDay(cellDate, today),
      highlight,
    });
  }

  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  let trailingDay = 1;

  while (cells.length % 7 !== 0) {
    const dateParts = { year: nextYear, month: nextMonth, day: trailingDay };
    const date = formatISODate(dateParts);
    const dayData = daysByDate.get(date) ?? buildDefaultDayData(dateParts, false, today);

    cells.push({
      ...dayData,
      day: trailingDay,
      isCurrentMonth: false,
      isToday: false,
    });

    trailingDay += 1;
  }

  return cells;
};

/** Monday-based weekday offsets used to render LU–DO headers. */
export const CALENDAR_WEEKDAY_OFFSETS = [0, 1, 2, 3, 4, 5, 6] as const;

const MONDAY_REFERENCE_UTC = Date.UTC(2024, 0, 1);

export const getCalendarWeekdayReferenceDate = (offset: number): Date =>
  new Date(MONDAY_REFERENCE_UTC + offset * 86_400_000);
